import { Link } from 'react-router-dom';
import { useState } from 'react';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

export default function ContactPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setStatus('sending');
    const data = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      data.append(key, value as string);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d724o39lum2co2q1g290', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#EEF4F7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#81D8D0]/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-24 sm:h-28">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/8518bd20f704ac7d2f3f65f4644eeb9a/37cbdfbab98dc394d9ee285d8ad1648c.svg"
                alt="PawBridge"
                className="w-40 h-40 sm:w-48 sm:h-48 object-contain brightness-0"
              />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/about" className="relative text-sm text-white hover:text-white transition-colors whitespace-nowrap group">
                PawBridgeについて
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/sitters" className="relative text-sm text-white hover:text-white transition-colors whitespace-nowrap group">
                シッターを探す
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/sitter-registration" className="relative text-sm text-white hover:text-white transition-colors whitespace-nowrap group">
                シッター登録
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/login" className="relative text-sm text-white hover:text-white transition-colors whitespace-nowrap group">
                ログイン
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/booking" className="px-6 py-2.5 bg-white text-[#81D8D0] text-sm font-medium rounded-full hover:bg-white/90 transition-all hover:shadow-lg whitespace-nowrap">
                予約する
              </Link>
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-white hover:text-white/80 transition-colors py-2">PawBridgeについて</Link>
              <Link to="/sitters" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-white hover:text-white/80 transition-colors py-2">シッターを探す</Link>
              <Link to="/sitter-registration" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-white hover:text-white/80 transition-colors py-2">シッター登録</Link>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-white hover:text-white/80 transition-colors py-2">ログイン</Link>
              <Link to="/booking" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-2.5 bg-white text-[#81D8D0] text-sm font-medium rounded-full hover:bg-white/90 transition-all text-center">予約する</Link>
            </div>
          )}
        </div>
      </nav>

      <div className="pt-28 sm:pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">

          {/* Header */}
          <div className="mb-8 md:mb-12">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#81D8D0]/20">
                <i className="ri-mail-send-line text-xl text-[#81D8D0]"></i>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#1B2A4A]">お問い合わせ</h1>
            </div>
            <p className="text-sm text-[#1B2A4A]/50">ご質問・ご要望・不具合報告など、お気軽にご連絡ください。通常2〜3営業日以内にご返信します。</p>
          </div>

          {/* Info cards */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {[
              { icon: 'ri-time-line', label: '返信目安', sub: '2〜3営業日以内' },
              { icon: 'ri-translate-2', label: '対応言語', sub: '日本語のみ' },
              { icon: 'ri-customer-service-2-line', label: 'サポート時間', sub: '平日 9:00〜18:00 (CET)' },
            ].map(card => (
              <div key={card.label} className="bg-[#81D8D0]/10 border border-[#81D8D0]/30 rounded-xl p-4 flex gap-3 items-start">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#81D8D0]/20 flex-shrink-0">
                  <i className={`${card.icon} text-lg text-[#81D8D0]`}></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1B2A4A]">{card.label}</p>
                  <p className="text-xs text-[#1B2A4A]/60 leading-relaxed mt-0.5">{card.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Success message */}
          {status === 'success' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-100 flex-shrink-0">
                <i className="ri-check-line text-xl text-emerald-600"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-emerald-700">送信完了しました！</p>
                <p className="text-xs text-emerald-600 mt-0.5">内容を確認の上、2〜3営業日以内にご登録のメールアドレスへご返信します。</p>
              </div>
            </div>
          )}

          {/* Error message */}
          {status === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0">
                <i className="ri-error-warning-line text-xl text-red-500"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-red-700">送信に失敗しました</p>
                <p className="text-xs text-red-600 mt-0.5">お手数ですが、時間をおいて再度お試しください。</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl p-6 md:p-8 shadow-sm space-y-5 md:space-y-6"
          >
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-[#1B2A4A] mb-2">
                お名前 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                placeholder="山田 太郎"
                className="w-full px-4 py-3 rounded-xl border border-[#D4E8EC] bg-[#EEF4F7] text-sm text-[#1B2A4A] placeholder-[#1B2A4A]/30 focus:outline-none focus:border-[#81D8D0] focus:bg-white transition-colors"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-[#1B2A4A] mb-2">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                placeholder="example@email.com"
                className="w-full px-4 py-3 rounded-xl border border-[#D4E8EC] bg-[#EEF4F7] text-sm text-[#1B2A4A] placeholder-[#1B2A4A]/30 focus:outline-none focus:border-[#81D8D0] focus:bg-white transition-colors"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-[#1B2A4A] mb-2">
                お問い合わせ種別 <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="category"
                  required
                  defaultValue=""
                  className="w-full px-4 py-3 rounded-xl border border-[#D4E8EC] bg-[#EEF4F7] text-sm text-[#1B2A4A] focus:outline-none focus:border-[#81D8D0] focus:bg-white transition-colors appearance-none cursor-pointer"
                >
                  <option value="" disabled>選択してください</option>
                  <option value="飼い主として利用">飼い主として利用に関して</option>
                  <option value="シッター登録・活動">シッター登録・活動に関して</option>
                  <option value="予約・支払い">予約・支払いに関して</option>
                  <option value="安心保証制度">安心保証制度に関して</option>
                  <option value="不具合・バグ報告">不具合・バグ報告</option>
                  <option value="アカウント問題">アカウント問題</option>
                  <option value="その他">その他</option>
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                  <i className="ri-arrow-down-s-line text-[#1B2A4A]/40"></i>
                </div>
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-semibold text-[#1B2A4A] mb-2">
                お問い合わせ内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                required
                rows={6}
                maxLength={500}
                placeholder="お問い合わせ内容を詳しくご記入ください..."
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full px-4 py-3 rounded-xl border border-[#D4E8EC] bg-[#EEF4F7] text-sm text-[#1B2A4A] placeholder-[#1B2A4A]/30 focus:outline-none focus:border-[#81D8D0] focus:bg-white transition-colors resize-none"
              />
              <div className="flex justify-end mt-1">
                <span className={`text-xs ${charCount >= 500 ? 'text-red-500 font-semibold' : 'text-[#1B2A4A]/40'}`}>
                  {charCount} / 500
                </span>
              </div>
              {charCount >= 500 && (
                <p className="text-xs text-red-500 mt-1">500文字以内でご入力ください。</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={status === 'sending' || charCount >= 500}
              className="w-full py-4 bg-[#81D8D0] text-white text-sm font-bold rounded-xl hover:bg-[#1B2A4A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              {status === 'sending' ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-lg"></i>
                  送信中...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line text-lg"></i>
                  送信する
                </>
              )}
            </button>

            <p className="text-xs text-[#1B2A4A]/40 text-center leading-relaxed">
              送信内容はPawBridgeの
              <Link to="/terms" className="text-[#81D8D0] hover:underline">利用規約</Link>
              および
              <Link to="/privacy" className="text-[#81D8D0] hover:underline">プライバシーポリシー</Link>
              に基づき適切に管理されます。
            </p>
          </form>

          <div className="mt-6 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/" className="inline-flex items-center gap-2 text-xs md:text-sm text-[#1B2A4A]/60 hover:text-[#1B2A4A] transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              ホームに戻る
            </Link>
            <span className="hidden sm:block text-[#1B2A4A]/30">|</span>
            <Link to="/about" className="inline-flex items-center gap-2 text-xs md:text-sm text-[#1B2A4A]/60 hover:text-[#1B2A4A] transition-colors cursor-pointer">
              PawBridgeについて
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#81D8D0] text-white pt-12 md:pt-20 pb-8 md:pb-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-12 mb-8 md:mb-12">
            <div>
              <img
                src="https://static.readdy.ai/image/8518bd20f704ac7d2f3f65f4644eeb9a/37cbdfbab98dc394d9ee285d8ad1648c.svg"
                alt="PawBridge"
                className="h-28 md:h-36 mb-4 md:mb-5 brightness-0"
              />
              <p className="text-xs md:text-sm text-white/80 leading-relaxed">海外在住日本人のためのペットシッターコミュニティ</p>
            </div>
            <div>
              <h4 className="text-xs md:text-sm font-semibold uppercase mb-4 md:mb-6">サービス</h4>
              <ul className="space-y-3 md:space-y-4">
                <li><Link to="/sitters" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors">シッター検索</Link></li>
                <li><Link to="/sitter-registration" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors">シッター登録</Link></li>
                <li><Link to="/booking" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors">料金</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs md:text-sm font-semibold uppercase mb-4 md:mb-6">会社情報</h4>
              <ul className="space-y-3 md:space-y-4">
                <li><Link to="/about" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors">PawBridgeについて</Link></li>
                <li><Link to="/contact" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors">お問い合わせ</Link></li>
                <li><Link to="/terms" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors">PawBridge 利用規約</Link></li>
                <li><Link to="/sitter-terms" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors">PawBridge シッター利用規約</Link></li>
                <li><Link to="/guarantee" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors">PawBridge 安心保証規約</Link></li>
                <li><Link to="/privacy" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors">プライバシーポリシー</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-6 md:pt-8 border-t border-white/30 text-center">
            <p className="text-xs text-white/70">© 2025 PawBridge. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
